import { Tooltip } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { LuStar } from 'react-icons/lu'

import { useNotification } from '@/Core/Hooks/useNotification'
import { Button } from '@/Core/UI/Button'
import { favoritesManager } from '@/Ship/Stores/FavoritesManager'

/** Бек тримає назву обраного до 150 символів. */
const TITLE_MAX = 150

/** Те, що зірочці треба від контексту картки. */
export interface FavoriteCardContext {
    formContext?: { values?: Record<string, unknown> } | undefined
}

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value.trim() : null)

/**
 * Назва, під якою запис стоятиме в обраному.
 *
 * Співробітник — «Прізвище Ім'я», як у легасі (`SPEC-014` §4.3, П7): у картці
 * співробітника `title` немає, а `fullName` несе ще й по батькові. Клієнт і
 * перевізник — своя назва. Номер, якщо запис його має, — попереду.
 */
export const favoriteTitle = (resourceKey: string, values: Record<string, unknown>): string => {
    const person =
        resourceKey === 'Setting.User'
            ? [text(values['lastName']), text(values['firstName'])].filter(Boolean).join(' ') || null
            : null
    const name = person ?? text(values['title']) ?? text(values['fullName']) ?? text(values['code']) ?? 'Без назви'
    const number = values['number']
    const title = typeof number === 'number' ? `№${number} · ${name}` : name

    return title.length > TITLE_MAX ? `${title.slice(0, TITLE_MAX - 1)}…` : title
}

interface FavoriteToggleProps {
    resourceKey: string
    crudContext: FavoriteCardContext
}

/**
 * Зірочка «в обране» для відкритої картки.
 *
 * Заповнюється одразу після кліку, не чекаючи сервера; не вдалося — повертається
 * назад, і про це каже повідомлення.
 *
 * ⚠️ Поки запит у польоті, кнопка в стані `loading`. Раніше вона лишалася
 * клікабельною, а стор мовчки ігнорував другий клік (запис ще `pending`) —
 * швидке «зняти й поставити знову» губило другу дію без жодного сліду.
 */
export const FavoriteToggle = observer(({ resourceKey, crudContext }: FavoriteToggleProps) => {
    const { showError } = useNotification()

    useEffect(() => {
        favoritesManager.ensureLoaded().catch((error: unknown) => {
            console.warn('FavoriteToggle: не вдалося прочитати обране', error)
        })
    }, [])

    const values = crudContext.formContext?.values ?? {}
    const uuid = text(values['uuid'])

    if (!uuid) return null

    const isFavorite = Boolean(favoritesManager.find(resourceKey, uuid))
    const isPending = favoritesManager.isPending(resourceKey, uuid)
    const label = isFavorite ? 'Прибрати з обраного' : 'Додати до обраного'

    const toggle = async (): Promise<void> => {
        try {
            // Перший клік міг випередити читання списку — рішення лише за свіжим станом
            await favoritesManager.ensureLoaded()

            const favorite = favoritesManager.find(resourceKey, uuid)

            if (favorite) {
                await favoritesManager.remove(favorite)
            } else {
                await favoritesManager.add(resourceKey, uuid, favoriteTitle(resourceKey, values))
            }
        } catch {
            showError(isFavorite ? 'Не вдалося прибрати з обраного' : 'Не вдалося додати до обраного')
        }
    }

    return (
        <Tooltip title={label} placement="bottom">
            <span style={{ display: 'inline-flex' }}>
                <Button
                    className="button-group-item"
                    size="middle"
                    aria-label={label}
                    aria-pressed={isFavorite}
                    aria-busy={isPending}
                    loading={isPending}
                    icon={
                        <LuStar
                            strokeWidth={1.75}
                            style={{ fontSize: 18, ...(isFavorite ? { color: 'var(--ant-color-warning)' } : {}) }}
                            fill={isFavorite ? 'currentColor' : 'none'}
                        />
                    }
                    onClick={() => void toggle()}
                />
            </span>
        </Tooltip>
    )
})
